import React, { useEffect, useState, useRef } from 'react';
import {
    ArrowRight, ArrowUp,
    Instagram, Youtube, Music2, Facebook, Twitter,
    Flame, Sparkles, Network, Rocket, Layers,
    Hammer, Zap, Award,
    Workflow, BadgeCheck, ShieldCheck,
    Beaker, Target, Crown,
    PlayCircle, Repeat,
} from 'lucide-react';
import { GlassCard } from './reports/_shared/GlassCard';

// ───────────────────────────────────────────────────────────────────
// Counter, animates 0 to end when scrolled into view
// ───────────────────────────────────────────────────────────────────
const useCounter = (end, duration = 1400) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    const start = performance.now();
                    const animate = (now) => {
                        const progress = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - progress, 3);
                        setCount(Math.floor(end * eased));
                        if (progress < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.25 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);

    return [count, ref];
};

const AnimatedNumber = ({ value, suffix = "", className = "" }) => {
    const [count, ref] = useCounter(value);
    return (
        <span ref={ref} className={className}>{count.toLocaleString()}{suffix}</span>
    );
};

const SectionLabel = ({ children }) => (
    <p className="text-xs uppercase tracking-[0.3em] text-indigo-300/70 font-mono mb-4">{children}</p>
);

const Pill = ({ icon: Ico, children, tone = "indigo" }) => {
    const map = {
        indigo: "bg-indigo-500/10 border-indigo-500/20 text-indigo-200",
        amber: "bg-amber-500/10 border-amber-500/30 text-amber-200",
        muted: "bg-white/5 border-white/10 text-white/60",
    };
    return (
        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium tracking-wide border ${map[tone]}`}>
            {Ico && <Ico className="w-3.5 h-3.5" />}
            {children}
        </span>
    );
};

// ───────────────────────────────────────────────────────────────────
// Data blocks
// ───────────────────────────────────────────────────────────────────

const promiseCards = [
    {
        icon: Repeat,
        title: "We use what you already have",
        body: "13 clips a week from your editor. 134 episodes of source IP. Your viral playbook is already written.",
    },
    {
        icon: Target,
        title: "We learn your winning style",
        body: "Your top-performing clips become the template. We reverse-engineer the hooks, the pacing, the captions that hit.",
    },
    {
        icon: Network,
        title: "We replicate, 10 ways",
        body: "10 themed fan pages, 5 platforms each. Same source material. 10 times the surface area.",
    },
];

const mathStats = [
    { value: "45,000", label: "videos / month", sub: "at full scale" },
    { value: "1%", label: "viral rate", sub: "industry baseline" },
    { value: "450", label: "outliers / month", sub: "from the 1% rule" },
    { value: "~75K", label: "avg views / outlier", sub: "conservative" },
];

const tierData = [
    {
        tier: "Tier 1",
        name: "Premium IP",
        icon: Crown,
        color: "indigo",
        cadence: "1 to 2 posts / day",
        bullets: [
            "Polished pages that match your main page in quality.",
            "Only top-quality content lives here.",
            "Content lands here AFTER it's proven on T2 and T3.",
            "Each page becomes its own premium IP that compounds over time.",
        ],
    },
    {
        tier: "Tier 2",
        name: "Volume + Quality",
        icon: Layers,
        color: "purple",
        cadence: "3 to 5 posts / day",
        bullets: [
            "Captioned clips, well-edited, mid-tier polish.",
            "This is where promising T3 winners get scaled.",
            "Best-performing T2 clips get promoted up to T1 with extra polish.",
            "The bulk of network volume lives at this layer.",
        ],
    },
    {
        tier: "Tier 3",
        name: "The Lab",
        icon: Beaker,
        color: "amber",
        cadence: "5 to 10 posts / day",
        bullets: [
            "Experimental. Every new clip starts here.",
            "We run rapid hook tests, caption variants, audio swaps.",
            "Whatever performs gets promoted to T2.",
            "Whatever doesn't gets killed without ceremony.",
        ],
    },
];

const responsibilityCards = [
    {
        icon: ShieldCheck,
        title: "No new recording days",
        body: "We work with the clips your editor already cuts. Your studio calendar stays exactly as it is.",
    },
    {
        icon: Workflow,
        title: "No page management",
        body: "Logins, posting, hashtags, captions, growth, replies. Every page, every platform, owned and run by us.",
    },
    {
        icon: Zap,
        title: "Push what you can't on your main page",
        body: "Experimental hooks, edgier angles, the stuff your main IP can't risk. The network becomes your testing ground.",
    },
];

const badshahStats = [
    { value: "5", label: "IG fan pages", sub: "built from scratch" },
    { value: "250", label: "videos / month", sub: "across the network" },
    { value: "42M", label: "views in 2 months", sub: "compound curve" },
    { value: "0", label: "starting followers", sub: "across all 5 pages" },
];

const phases = [
    {
        icon: Hammer,
        label: "Phase 1",
        weeks: "Weeks 1 to 2",
        title: "Foundation",
        bullets: [
            "ICP lock + objective doc",
            "Style audit of your top-performing episodes",
            "Network architecture and naming",
            "3 pilot fan pages launched across 3 platforms = 9 deployments",
        ],
        accent: "indigo",
    },
    {
        icon: Rocket,
        label: "Phase 2",
        weeks: "Weeks 3 to 6",
        title: "Scale",
        bullets: [
            "Pilot pages live and posting daily",
            "Identify which page and platform combos are sticking",
            "Scale to 10 fan pages × 5 platforms",
            "T3 to T2 promotion engine begins",
        ],
        accent: "indigo",
    },
    {
        icon: Flame,
        label: "Phase 3",
        weeks: "Weeks 7 to 12",
        title: "Compound",
        bullets: [
            "Full network running at 45K videos per month",
            "T3 to T2 to T1 promotion engine in steady state",
            "~50M views per month at peak compounding",
            "Month 3 is when the curve breaks open",
        ],
        accent: "amber",
    },
];

const whyCards = [
    { icon: Workflow, title: "Day-one capacity", body: "We ship, not hire. Same team that ran Abhiveer's network from scratch." },
    { icon: BadgeCheck, title: "Distribution-only DNA", body: "We don't pretend to be your editor. We run the network around your existing IP." },
    { icon: Layers, title: "Single point of accountability", body: "Strategy, posting, reporting. One operator. One number." },
];

// Compounding chart data, bars sum to 100M total in 3 months
const compoundBars = [
    { month: "Month 1", value: 20, label: "~20M views" },
    { month: "Month 2", value: 30, label: "~30M views" },
    { month: "Month 3", value: 50, label: "~50M views" },
];

const platformIcons = [
    { icon: Instagram, name: "IG", color: "text-pink-300" },
    { icon: Youtube, name: "YT Shorts", color: "text-red-300" },
    { icon: Music2, name: "TikTok", color: "text-cyan-300" },
    { icon: Facebook, name: "FB Reels", color: "text-blue-300" },
    { icon: Twitter, name: "X", color: "text-white/70" },
];

const tierColorMap = {
    indigo: {
        border: "border-indigo-500/40",
        bg: "bg-indigo-500/[0.06]",
        text: "text-indigo-300",
        textBright: "text-indigo-200",
        bullet: "bg-indigo-400",
        ring: "ring-indigo-500/30",
    },
    purple: {
        border: "border-purple-500/40",
        bg: "bg-purple-500/[0.06]",
        text: "text-purple-300",
        textBright: "text-purple-200",
        bullet: "bg-purple-400",
        ring: "ring-purple-500/30",
    },
    amber: {
        border: "border-amber-500/40",
        bg: "bg-amber-500/[0.06]",
        text: "text-amber-300",
        textBright: "text-amber-200",
        bullet: "bg-amber-400",
        ring: "ring-amber-500/30",
    },
};

// ───────────────────────────────────────────────────────────────────
// Main component
// ───────────────────────────────────────────────────────────────────

export default function ChrisPocketProposal() {
    useEffect(() => {
        document.title = "SocialSEO × The Pocket · 100M Views in 3 Months";
    }, []);

    return (
        <div className="min-h-screen bg-transparent font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-32">
            <div className="fixed inset-0 pointer-events-none z-0" style={{
                background: `radial-gradient(circle at 80% 10%, rgba(99, 102, 241, 0.22), transparent 55%),
                             radial-gradient(circle at 15% 85%, rgba(217, 119, 6, 0.18), transparent 60%)`
            }}></div>

            <div className="relative z-10">
                <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-transparent pointer-events-none">
                    <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                        <span className="font-bold tracking-tight text-white">Social<span className="text-indigo-400">SEO</span></span>
                    </div>
                    <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                        <span className="text-xs text-white/70 font-mono tracking-widest uppercase">The Pocket · Distribution Network</span>
                    </div>
                </header>

                <main className="container mx-auto px-4 pt-32 space-y-28">

                    {/* ═══════════════════════════════════════════════════════ */}
                    {/* 1 · HERO */}
                    {/* ═══════════════════════════════════════════════════════ */}
                    <section className="text-center max-w-5xl mx-auto">
                        <SectionLabel>For Chris Griffin</SectionLabel>
                        <h1 className="text-7xl md:text-9xl font-black tracking-tight leading-[0.95] mb-6">
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-100 to-indigo-300">
                                The Pocket Plan
                            </span>
                        </h1>
                        <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.05] mb-6">
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-200 via-white to-amber-200">
                                100 Million Views in 3 Months
                            </span>
                        </h2>
                        <p className="text-lg md:text-xl text-indigo-200/90 mb-10 max-w-2xl mx-auto italic font-light">
                            Let's build distribution networks.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            <Pill icon={Hammer} tone="indigo">Built from scratch</Pill>
                            <Pill icon={Repeat} tone="indigo">Same recordings, 10× the reach</Pill>
                            <Pill icon={ShieldCheck} tone="amber">Zero accountability on you</Pill>
                        </div>
                    </section>

                    {/* ═══════════════════════════════════════════════════════ */}
                    {/* 2 · CASE STUDY · Abhiveer "From Zero" */}
                    {/* ═══════════════════════════════════════════════════════ */}
                    <section className="max-w-7xl mx-auto">
                        <SectionLabel>Abhiveer case study</SectionLabel>
                        <h2 className="text-4xl md:text-6xl font-bold mb-4 max-w-5xl leading-[1.05]">
                            Same podcast. Same editor. <span className="text-indigo-300">0 starting followers across 9 fan pages.</span>
                        </h2>
                        <p className="text-white/60 text-lg mb-10 max-w-2xl">51 days. Here's what happened.</p>

                        {/* Before / After split */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                            <GlassCard className="bg-white/[0.02]">
                                <p className="text-white/50 font-mono text-xs tracking-widest uppercase mb-4">Before · March 24, 2026</p>
                                <div className="space-y-4">
                                    <div className="flex items-baseline justify-between border-b border-white/5 pb-3">
                                        <span className="text-white/60 text-sm">Podcast IP</span>
                                        <span className="text-2xl font-black text-white/80">1</span>
                                    </div>
                                    <div className="flex items-baseline justify-between border-b border-white/5 pb-3">
                                        <span className="text-white/60 text-sm">Fan pages</span>
                                        <span className="text-2xl font-black text-white/40">0</span>
                                    </div>
                                    <div className="flex items-baseline justify-between border-b border-white/5 pb-3">
                                        <span className="text-white/60 text-sm">Network views</span>
                                        <span className="text-2xl font-black text-white/40">0</span>
                                    </div>
                                    <div className="flex items-baseline justify-between">
                                        <span className="text-white/60 text-sm">Posts shipped</span>
                                        <span className="text-2xl font-black text-white/40">0</span>
                                    </div>
                                </div>
                            </GlassCard>

                            <GlassCard className="bg-indigo-500/[0.05]">
                                <p className="text-indigo-300 font-mono text-xs tracking-widest uppercase mb-4">After · May 13, 2026 · 51 days</p>
                                <div className="space-y-4">
                                    <div className="flex items-baseline justify-between border-b border-white/5 pb-3">
                                        <span className="text-white/60 text-sm">Accounts live</span>
                                        <span className="text-2xl font-black text-indigo-200">
                                            <AnimatedNumber value={11} />
                                        </span>
                                    </div>
                                    <div className="flex items-baseline justify-between border-b border-white/5 pb-3">
                                        <span className="text-white/60 text-sm">Posts shipped</span>
                                        <span className="text-2xl font-black text-indigo-200">
                                            <AnimatedNumber value={595} />
                                        </span>
                                    </div>
                                    <div className="flex items-baseline justify-between border-b border-white/5 pb-3">
                                        <span className="text-white/60 text-sm">Network views</span>
                                        <span className="text-3xl md:text-4xl font-black text-indigo-100">
                                            <AnimatedNumber value={536405} />
                                        </span>
                                    </div>
                                    <div className="flex items-baseline justify-between">
                                        <span className="text-white/60 text-sm">Avg views / post</span>
                                        <span className="text-2xl font-black text-indigo-200">
                                            <AnimatedNumber value={902} />
                                        </span>
                                    </div>
                                </div>
                            </GlassCard>
                        </div>

                        {/* Single fan-page hero card */}
                        <GlassCard className="bg-gradient-to-br from-amber-500/[0.08] to-indigo-500/[0.04] relative overflow-hidden">
                            <div className="absolute -right-8 -top-8 text-[160px] font-black text-amber-500/[0.05] pointer-events-none leading-none select-none">519×</div>
                            <div className="flex flex-col md:flex-row items-start md:items-center gap-6 relative">
                                <div className="bg-amber-500/20 p-4 rounded-2xl flex-shrink-0">
                                    <Award className="w-10 h-10 text-amber-300" />
                                </div>
                                <div className="flex-grow">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Pill icon={Sparkles} tone="amber">One page. 51 days in.</Pill>
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-black text-white mb-3">
                                        @unseenabhiveer <span className="text-amber-300">·</span> <span className="text-amber-200">339,565 views</span>
                                    </h3>
                                    <div className="grid grid-cols-3 gap-4 mt-4">
                                        <div>
                                            <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold mb-1">Followers</p>
                                            <p className="text-2xl font-black text-white">655</p>
                                        </div>
                                        <div>
                                            <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold mb-1">Posts</p>
                                            <p className="text-2xl font-black text-white">60</p>
                                        </div>
                                        <div>
                                            <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold mb-1">Views / follower</p>
                                            <p className="text-2xl font-black text-amber-200">519×</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </GlassCard>

                        <p className="text-center text-white/60 text-base mt-6 max-w-3xl mx-auto">
                            And this is just <span className="text-indigo-300 font-semibold">one of 9 fan pages</span> running across 3 platforms.
                        </p>
                    </section>

                    {/* ═══════════════════════════════════════════════════════ */}
                    {/* 3 · THE 10× PROMISE */}
                    {/* ═══════════════════════════════════════════════════════ */}
                    <section className="max-w-7xl mx-auto">
                        <SectionLabel>What we'd do for you</SectionLabel>
                        <h2 className="text-3xl md:text-5xl font-bold mb-10 max-w-4xl leading-tight">
                            We <span className="text-indigo-300">10× the views</span> without you recording one extra second.
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
                            {promiseCards.map((c, i) => (
                                <GlassCard key={i}>
                                    <c.icon className="w-9 h-9 text-indigo-300 mb-4" />
                                    <p className="font-bold text-white text-lg mb-3">{c.title}</p>
                                    <p className="text-white/65 text-sm leading-relaxed">{c.body}</p>
                                </GlassCard>
                            ))}
                        </div>

                        {/* Hero stat */}
                        <GlassCard className="text-center py-10 bg-gradient-to-br from-indigo-500/[0.08] to-amber-500/[0.04]">
                            <p className="text-white/50 font-mono text-xs tracking-[0.3em] uppercase mb-3">Net result</p>
                            <p className="text-5xl md:text-7xl font-black bg-clip-text text-transparent bg-gradient-to-r from-indigo-200 via-white to-amber-200 leading-none">
                                100,000,000
                            </p>
                            <p className="text-white/70 text-lg mt-3">views <span className="text-white/40">·</span> 3 months <span className="text-white/40">·</span> 0 extra recording days</p>
                        </GlassCard>
                    </section>

                    {/* ═══════════════════════════════════════════════════════ */}
                    {/* 4 · THE MATH */}
                    {/* ═══════════════════════════════════════════════════════ */}
                    <section className="max-w-7xl mx-auto">
                        <SectionLabel>The math, end to end</SectionLabel>
                        <h2 className="text-3xl md:text-5xl font-bold mb-10 max-w-4xl leading-tight">
                            10 pages <span className="text-indigo-300">×</span> 5 platforms <span className="text-indigo-300">×</span> 3 to 5 posts a day = <span className="text-indigo-300">45,000 videos a month.</span>
                        </h2>

                        {/* Platform row */}
                        <GlassCard className="mb-8">
                            <p className="text-white/50 text-xs uppercase tracking-widest font-semibold text-center mb-5">Five distribution platforms</p>
                            <div className="flex items-center justify-center gap-6 md:gap-10 flex-wrap">
                                {platformIcons.map((p, i) => (
                                    <div key={i} className="flex flex-col items-center gap-2">
                                        <p.icon className={`w-8 h-8 ${p.color}`} />
                                        <span className="text-white/70 text-xs font-semibold">{p.name}</span>
                                    </div>
                                ))}
                            </div>
                        </GlassCard>

                        {/* Math stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                            {mathStats.map((s, i) => (
                                <GlassCard key={i} className="text-center">
                                    <p className="text-3xl md:text-4xl font-black text-indigo-200">{s.value}</p>
                                    <p className="text-white/85 font-semibold text-sm mt-1">{s.label}</p>
                                    <p className="text-white/40 text-xs mt-1">{s.sub}</p>
                                </GlassCard>
                            ))}
                        </div>

                        {/* Compounding callout */}
                        <GlassCard>
                            <div className="text-center mb-8">
                                <h3 className="text-2xl md:text-4xl font-bold leading-tight">
                                    <span className="text-white/70">Month 1 is the worst month.</span>{" "}
                                    <span className="text-indigo-300">Month 3 is the best.</span>
                                </h3>
                                <p className="text-white/60 text-sm mt-3 max-w-2xl mx-auto">
                                    Networks compound. Each page builds its own audience, refines its own hooks, then feeds wins back into the system.
                                </p>
                            </div>

                            {/* Bar chart */}
                            <div className="grid grid-cols-3 gap-4 mt-8 items-end h-64">
                                {compoundBars.map((b, i) => {
                                    const heightPct = (b.value / 55) * 100;
                                    const intensity = i === 0 ? 'bg-indigo-500/40' : i === 1 ? 'bg-indigo-500/60' : 'bg-amber-500/70';
                                    return (
                                        <div key={i} className="flex flex-col items-center gap-3 h-full justify-end">
                                            <div className="text-center mb-1">
                                                <p className={`text-2xl md:text-3xl font-black ${i === 2 ? 'text-amber-200' : 'text-indigo-200'}`}>{b.label}</p>
                                            </div>
                                            <div
                                                className={`${intensity} w-full rounded-t-xl transition-all`}
                                                style={{ height: `${heightPct}%`, minHeight: '40px' }}
                                            />
                                            <p className="text-white/60 text-sm font-semibold">{b.month}</p>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Total line */}
                            <div className="text-center mt-10 pt-8 border-t border-white/10">
                                <p className="text-white/50 text-xs font-mono uppercase tracking-widest mb-2">3-month total</p>
                                <p className="text-4xl md:text-6xl font-black">
                                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-200 via-white to-amber-200">
                                        100,000,000 views
                                    </span>
                                </p>
                            </div>
                        </GlassCard>
                    </section>

                    {/* ═══════════════════════════════════════════════════════ */}
                    {/* 5 · THREE-TIER SYSTEM */}
                    {/* ═══════════════════════════════════════════════════════ */}
                    <section className="max-w-6xl mx-auto">
                        <SectionLabel>The system</SectionLabel>
                        <h2 className="text-3xl md:text-5xl font-bold mb-4 max-w-4xl leading-tight">
                            Three tiers. One funnel. <span className="text-indigo-300">Winners ride up.</span>
                        </h2>
                        <p className="text-white/55 text-sm mb-10 max-w-2xl">Every new clip starts at the bottom. The ones that hit get promoted up the pyramid.</p>

                        {/* Pyramid layout, T1 at apex, T3 at base */}
                        <div className="space-y-3">
                            {tierData.map((t, i) => {
                                const c = tierColorMap[t.color];
                                // i=0 -> T1 (narrowest), i=1 -> T2 (medium), i=2 -> T3 (widest)
                                const widthClass = i === 0 ? 'max-w-md' : i === 1 ? 'max-w-2xl' : 'max-w-4xl';
                                return (
                                    <React.Fragment key={i}>
                                        <div className={`${widthClass} mx-auto w-full`}>
                                            <GlassCard className={`${c.bg}`}>
                                                <div className="flex items-start gap-4 mb-4">
                                                    <div className={`p-3 rounded-2xl ${c.bg} flex-shrink-0`}>
                                                        <t.icon className={`w-7 h-7 ${c.text}`} />
                                                    </div>
                                                    <div className="flex-grow">
                                                        <p className={`font-mono text-xs tracking-widest ${c.text} uppercase`}>{t.tier}</p>
                                                        <h3 className="text-2xl font-black text-white">{t.name}</h3>
                                                        <p className={`${c.textBright} text-xs mt-1 font-semibold`}>{t.cadence}</p>
                                                    </div>
                                                </div>
                                                <ul className="space-y-2">
                                                    {t.bullets.map((b, j) => (
                                                        <li key={j} className="flex items-start gap-2 text-white/75 text-sm leading-relaxed">
                                                            <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${c.bullet} flex-shrink-0`} />
                                                            <span>{b}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </GlassCard>
                                        </div>
                                        {i < tierData.length - 1 && (
                                            <div className="flex justify-center">
                                                <ArrowUp className="w-6 h-6 text-white/40" />
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        <p className="text-center text-white/55 text-sm mt-10 max-w-2xl mx-auto">
                            Each page gets a custom strategy after we lock your objective and ICP.
                        </p>
                    </section>

                    {/* ═══════════════════════════════════════════════════════ */}
                    {/* 6 · ZERO RESPONSIBILITY */}
                    {/* ═══════════════════════════════════════════════════════ */}
                    <section className="max-w-7xl mx-auto">
                        <SectionLabel>What you'd own (basically nothing)</SectionLabel>
                        <h2 className="text-3xl md:text-5xl font-bold mb-10 max-w-4xl leading-tight">
                            Every fan page is <span className="text-indigo-300">built, run, and managed by us.</span>
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                            {responsibilityCards.map((c, i) => (
                                <GlassCard key={i}>
                                    <div className="flex items-start gap-3 mb-4">
                                        <c.icon className="w-7 h-7 text-indigo-300 flex-shrink-0" />
                                    </div>
                                    <p className="font-bold text-white text-lg mb-2">{c.title}</p>
                                    <p className="text-white/65 text-sm leading-relaxed">{c.body}</p>
                                </GlassCard>
                            ))}
                        </div>

                        <p className="text-center text-white/70 text-base max-w-3xl mx-auto leading-relaxed">
                            You carry zero responsibility for any of these pages. Your name doesn't go on a single one unless you want it to.
                        </p>
                    </section>

                    {/* ═══════════════════════════════════════════════════════ */}
                    {/* 7 · BADSHAH */}
                    {/* ═══════════════════════════════════════════════════════ */}
                    <section className="max-w-6xl mx-auto">
                        <SectionLabel>Same playbook, different artist</SectionLabel>
                        <h2 className="text-3xl md:text-5xl font-bold mb-3 max-w-4xl leading-tight">
                            Badshah <span className="text-amber-300">·</span> <span className="text-white/70">Indian hip-hop.</span>
                        </h2>
                        <p className="text-white/55 text-base mb-8 max-w-2xl">Same playbook. Different vertical.</p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {badshahStats.map((s, i) => (
                                <GlassCard key={i} className={`text-center ${i === 2 ? 'bg-amber-500/[0.08]' : ''}`}>
                                    <p className={`text-4xl md:text-5xl font-black ${i === 2 ? 'text-amber-200' : 'text-white'}`}>{s.value}</p>
                                    <p className="text-white/85 font-semibold text-sm mt-2">{s.label}</p>
                                    <p className="text-white/40 text-xs mt-1">{s.sub}</p>
                                </GlassCard>
                            ))}
                        </div>
                    </section>

                    {/* ═══════════════════════════════════════════════════════ */}
                    {/* 8 · 90-DAY ROLLOUT */}
                    {/* ═══════════════════════════════════════════════════════ */}
                    <section className="max-w-7xl mx-auto">
                        <SectionLabel>The 90-day rollout</SectionLabel>
                        <h2 className="text-3xl md:text-5xl font-bold mb-10 max-w-4xl leading-tight">
                            Build, scale, <span className="text-indigo-300">compound.</span>
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {phases.map((ph, i) => {
                                const isAmber = ph.accent === 'amber';
                                return (
                                    <GlassCard key={i} className={`${isAmber ? 'bg-amber-500/[0.05]' : ''}`}>
                                        <div className="flex items-start gap-4 mb-5">
                                            <ph.icon className={`w-10 h-10 flex-shrink-0 ${isAmber ? 'text-amber-300' : 'text-indigo-300'}`} />
                                            <div>
                                                <p className={`font-mono text-xs tracking-widest ${isAmber ? 'text-amber-300/70' : 'text-indigo-300/70'}`}>{ph.label} · {ph.weeks}</p>
                                                <h3 className="text-2xl font-black text-white">{ph.title}</h3>
                                            </div>
                                        </div>
                                        <ul className="space-y-2.5">
                                            {ph.bullets.map((b, j) => (
                                                <li key={j} className="flex items-start gap-2 text-white/75 text-sm leading-relaxed">
                                                    <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${isAmber ? 'bg-amber-400' : 'bg-indigo-400'}`} />
                                                    <span>{b}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </GlassCard>
                                );
                            })}
                        </div>
                    </section>

                    {/* ═══════════════════════════════════════════════════════ */}
                    {/* 9 · WHY US */}
                    {/* ═══════════════════════════════════════════════════════ */}
                    <section className="max-w-6xl mx-auto">
                        <SectionLabel>Why SocialSEO</SectionLabel>
                        <h2 className="text-3xl md:text-5xl font-bold mb-10 max-w-4xl leading-tight">
                            One team. One operator. <span className="text-indigo-300">Same playbook we ran for Abhiveer and Badshah.</span>
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                            {whyCards.map((w, i) => (
                                <GlassCard key={i}>
                                    <w.icon className="w-8 h-8 text-indigo-300 mb-3" />
                                    <p className="text-white font-bold text-base mb-2">{w.title}</p>
                                    <p className="text-white/60 text-sm leading-relaxed">{w.body}</p>
                                </GlassCard>
                            ))}
                        </div>

                        <p className="text-center text-indigo-200/80 text-base italic">
                            Your editor stays. We build everything else.
                        </p>
                    </section>

                    {/* ═══════════════════════════════════════════════════════ */}
                    {/* 10 · CTA */}
                    {/* ═══════════════════════════════════════════════════════ */}
                    <section className="max-w-5xl mx-auto">
                        <GlassCard className="text-center py-14 bg-gradient-to-br from-indigo-500/[0.08] via-transparent to-amber-500/[0.08]">
                            <SectionLabel>Next step</SectionLabel>
                            <h2 className="text-3xl md:text-5xl font-black mb-4 max-w-3xl mx-auto leading-tight">
                                Let's finalize <span className="text-indigo-300">the plan</span> for you, Chris.
                            </h2>
                            <p className="text-white/65 text-base md:text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
                                15 minutes. Live walkthrough of Abhiveer's dashboard. Custom 3-month roadmap for The Pocket. No deck.
                            </p>

                            <a
                                href="https://calendly.com/suresh-socialseo/socialseo-instant-report-clone-1"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-indigo-500 to-amber-500 text-white font-bold rounded-full text-lg hover:scale-105 hover:shadow-2xl hover:shadow-indigo-500/30 transition-all duration-300"
                            >
                                <PlayCircle className="w-6 h-6" />
                                Book the call
                                <ArrowRight className="w-6 h-6" />
                            </a>
                            <p className="text-white/40 text-xs mt-5 font-mono tracking-widest uppercase">
                                15 min <span className="text-white/30">·</span> live data <span className="text-white/30">·</span> zero commitment
                            </p>
                        </GlassCard>
                    </section>

                    {/* Footer */}
                    <footer className="max-w-5xl mx-auto text-center pt-8 border-t border-white/5">
                        <p className="text-white/40 text-sm">
                            SocialSEO LLP <span className="text-white/30">·</span> Delhi <span className="text-white/30">·</span> 2026 <span className="text-white/30">·</span> Prepared for <span className="text-indigo-300">Chris Griffin · The Pocket</span> <span className="text-white/30">·</span> @thepocketpodcast
                        </p>
                    </footer>
                </main>
            </div>
        </div>
    );
}
